import { useId, type ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';

/**
 * Everything the control needs so the label, the description and the error
 * belong to it — spread it onto the control and the wiring is done.
 */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
}

interface FieldOwnProps {
  label: ReactNode;
  /** Under the label, before the control: what the field is for, or what shape it wants. */
  description?: ReactNode;
  /**
   * What is wrong with what is in the field. Shown under the control, and
   * announced the moment it appears — a message that only arrives visually is
   * a message the person filling in the form never gets.
   */
  error?: ReactNode;
  /** Marks the label, and says so out loud for a reader who cannot see the asterisk. */
  required?: boolean;
  className?: string;
}

/**
 * A label, a control, and the two lines that explain it.
 *
 * The control is handed its own wiring rather than finding it: `children` is a
 * function receiving the id and the aria attributes, and spreading them is
 * what makes the label point at the right element. Nothing is injected through
 * a context, so a field holding two controls is not silently wrong — it is the
 * case where you pass `htmlFor` yourself and render the controls directly.
 */
export type FieldProps = FieldOwnProps &
  (
    | {
        /** Left out: `Field` generates one and hands it to the render function. */
        htmlFor?: undefined;
        children: (control: FieldControlProps) => ReactNode;
      }
    | {
        /**
         * The id of the control this label belongs to. For a field the render
         * function cannot describe: a group of radios, two halves of a name, a
         * control that owns its own id.
         */
        htmlFor: string;
        children: ReactNode;
      }
  );

export function Field({
  label,
  description,
  error,
  required = false,
  className,
  htmlFor,
  children,
}: FieldProps) {
  const generated = useId();
  const id = htmlFor ?? generated;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;

  /* A node is a message; `false`, `null` and `''` are a form saying nothing is wrong. */
  const invalid =
    error !== undefined && error !== null && error !== false && error !== '';

  const control: FieldControlProps = {
    id,
    'aria-describedby':
      [description ? descriptionId : null, invalid ? errorId : null]
        .filter((part) => part !== null)
        .join(' ') || undefined,
    'aria-invalid': invalid || undefined,
  };

  return (
    <div data-slot='field' className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className='text-sm font-semibold text-ink'>
        {label}
        {required && (
          <>
            <span aria-hidden='true'>&nbsp;*</span>
            <span className='sr-only'> (required)</span>
          </>
        )}
      </label>

      {description && (
        <p
          id={descriptionId}
          data-slot='field-description'
          className='text-sm text-ink-muted'
        >
          {description}
        </p>
      )}

      {typeof children === 'function' ? children(control) : children}

      {invalid && (
        <p
          id={errorId}
          data-slot='field-error'
          role='alert'
          className='text-sm text-danger'
        >
          {error}
        </p>
      )}
    </div>
  );
}
